'use client';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { openobserveRum } from '@openobserve/browser-rum';

/**
 * Hook to track route changes in OpenObserve RUM
 *
 * This hook monitors pathname changes and manually starts new views in OpenObserve.
 * It's necessary when using `trackViewsManually: true` to prevent automatic
 * view names with '????' for dynamic routes.
 */
export const useOpenObserveViewTracking = () => {
	const pathname = usePathname();
	const previousPathnameRef = useRef<string | null>(null);
	const isInitialMount = useRef(true);

	useEffect(() => {
		// Skip initial mount (handled by OpenObserveProvider initialization)
		if (isInitialMount.current) {
			isInitialMount.current = false;
			previousPathnameRef.current = pathname;
			return;
		}

		// Only track if pathname actually changed
		if (pathname === previousPathnameRef.current) {
			return;
		}

		try {
			// Clean pathname (remove query params if any)
			const cleanPath = pathname.includes('?')
				? pathname.split('?')[0]
				: pathname;

			// Start new view in OpenObserve
			openobserveRum.startView({
				name: cleanPath
			});

			// Update previous pathname
			previousPathnameRef.current = pathname;
		} catch (error) {
			console.error('OpenObserve: Failed to track view change', error);
		}
	}, [pathname]);
};
